<script setup>
/**
 * One thing the assistant has read, and the one number that says whether reading it was
 * worth anything.
 *
 * ROW GRAMMAR, deliberately the same as `AttentionRow`: **[mark] [name · what it taught]
 * [go]**. The section already taught the owner where to look on a row; a second grammar
 * on a second screen is a second thing to learn.
 *
 * THE NUMBER IS THE POINT, not decoration. A source that produced zero answers is not a
 * source — the retriever searches the generated question/answer pairs and never the
 * original text, so a document with nothing generated from it is invisible to the
 * assistant while sitting in a list titled "what your assistant has read". That is the
 * single most misleading state this screen can get into, so it is called out on the row
 * in amber rather than left to be inferred from a quiet "0".
 *
 * The whole row is the link. A chevron with a hit area of twelve pixels is a target you
 * have to aim at; a row is one you cannot miss.
 */
import { computed } from 'vue';

import { frontendURL } from 'dashboard/helper/URLHelper';

import { useWasloI18n } from '../i18n.js';
import { answerCountLabel } from '../helpers/answer-count.js';
import { relativeTime } from '../helpers/relative-time.js';
import { toIntlLocale } from '../helpers/intl.js';

const props = defineProps({
  /** A row from `wasloApi.getDocuments()`. */
  source: { type: Object, required: true },
  /** Chatwoot account id. Null (mounted outside a matched route) → no link. */
  accountId: { type: Number, default: null },
});

const { t, locale } = useWasloI18n();

const intlLocale = computed(() => toIntlLocale(locale.value));

const answers = computed(() =>
  answerCountLabel(t, props.source.answerCount, value =>
    new Intl.NumberFormat(intlLocale.value).format(value)
  )
);

const taughtNothing = computed(() => props.source.answerCount <= 0);

const updated = computed(() => {
  const when = relativeTime(props.source.updatedAt, locale.value, {
    justNow: t('WASLO.OVERVIEW.STATUS.JUST_NOW'),
  });
  return when ? t('WASLO.ANSWERS.LIBRARY.UPDATED', { when }) : '';
});

const to = computed(() =>
  props.accountId === null
    ? null
    : frontendURL(
        `accounts/${props.accountId}/waslo/answers/${props.source.id}`
      )
);
</script>

<template>
  <!-- `flex` is load-bearing, not layout garnish: a bare `<li>` keeps `display: list-item`
       and Chatwoot's reset does not clear the marker here, so the row rendered with a
       bullet floating in the page gutter beside it. Every other list in this section
       happens to make its `<li>` a flex container and never saw it. -->
  <li class="flex" data-waslo-source :data-waslo-source-id="source.id">
    <component
      :is="to ? 'router-link' : 'div'"
      :to="to || undefined"
      class="flex items-center w-full gap-4 px-5 py-4 text-start"
      :class="to ? 'hover:bg-n-alpha-1' : ''"
    >
      <span
        class="grid rounded-lg shrink-0 place-items-center size-9"
        :class="taughtNothing ? 'bg-n-amber-3' : 'bg-n-blue-3'"
        aria-hidden="true"
      >
        <span
          class="size-4"
          :class="
            taughtNothing
              ? 'i-lucide-file-question text-n-amber-11'
              : 'i-lucide-file-text text-n-blue-10'
          "
        />
      </span>

      <div class="flex flex-col min-w-0 gap-1">
        <!-- The owner's own name for it. `<bdi>` rather than `dir="auto"` alone: an
             Arabic title inside an LTR list loses its punctuation to the row otherwise. -->
        <p
          class="mb-0 text-sm font-medium break-words text-n-slate-12"
          data-waslo-source-title
        >
          <bdi>{{ source.title }}</bdi>
        </p>
        <p class="mb-0 text-sm text-n-slate-11" data-waslo-source-meta>
          <span :class="taughtNothing ? 'text-n-amber-11' : ''">
            {{ answers }}
          </span>
          <template v-if="updated"> · {{ updated }}</template>
        </p>
        <p v-if="taughtNothing" class="mb-0 text-sm text-n-slate-11">
          {{ t('WASLO.ANSWERS.LIBRARY.NOTHING_LEARNED') }}
        </p>
      </div>

      <!-- Rotated rather than swapped for a left-pointing icon: one class, and it cannot
           fall out of step with the page direction the way two icon names can. -->
      <span
        v-if="to"
        class="ms-auto shrink-0 size-4 i-lucide-chevron-right text-n-slate-10 rtl:rotate-180"
        aria-hidden="true"
      />
    </component>
  </li>
</template>
